import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { adminAPI, customerAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorAlert from '../../components/ErrorAlert';

const renderInline = (text) => text.split(/(\[[^\]]+\]\([^)]+\)|`[^`]+`|\*\*[^*]+\*\*)/g).map((part, index) => {
  const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
  if (linkMatch) {
    return (
      <a
        key={index}
        href={linkMatch[2]}
        className="font-medium text-blue-700 underline decoration-blue-200 underline-offset-2 hover:text-blue-900"
      >
        {linkMatch[1]}
      </a>
    );
  }
  if (part.startsWith('`') && part.endsWith('`')) {
    return <code key={index} className="rounded bg-slate-100 px-1.5 py-0.5 text-sm text-slate-800">{part.slice(1, -1)}</code>;
  }
  if (part.startsWith('**') && part.endsWith('**')) {
    return <strong key={index} className="font-semibold text-slate-950">{part.slice(2, -2)}</strong>;
  }
  return part;
});

const MarkdownArticle = ({ content }) => {
  if (!content) return null;

  return (
    <article className="space-y-3 text-[15px] leading-7 text-slate-700">
      {content.split('\n').map((line, index) => {
        const trimmed = line.trim();
        const key = `${index}-${trimmed}`;

        if (!trimmed) return <div key={key} className="h-1" />;
        if (trimmed.startsWith('# ')) {
          return <h1 key={key} className="pt-1 text-3xl font-semibold tracking-tight text-slate-950">{trimmed.slice(2)}</h1>;
        }
        if (trimmed.startsWith('## ')) {
          return <h2 key={key} className="pt-6 text-xl font-semibold tracking-tight text-slate-950">{trimmed.slice(3)}</h2>;
        }
        if (trimmed.startsWith('### ')) {
          return <h3 key={key} className="pt-4 text-base font-semibold uppercase tracking-wide text-slate-800">{trimmed.slice(4)}</h3>;
        }
        if (trimmed.startsWith('#### ')) {
          return <h4 key={key} className="pt-3 text-sm font-semibold uppercase tracking-wide text-slate-700">{trimmed.slice(5)}</h4>;
        }
        if (/^\d+\.\s/.test(trimmed)) {
          return (
            <div key={key} className="flex gap-3">
              <span className="mt-1 flex h-6 min-w-6 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-700">
                {trimmed.match(/^(\d+)\./)?.[1]}
              </span>
              <span>{renderInline(trimmed.replace(/^\d+\.\s/, ''))}</span>
            </div>
          );
        }
        if (trimmed.startsWith('- ')) {
          return (
            <div key={key} className="flex gap-3">
              <span className="mt-3 h-1.5 w-1.5 flex-none rounded-full bg-blue-500" />
              <span>{renderInline(trimmed.slice(2))}</span>
            </div>
          );
        }

        return <p key={key}>{renderInline(trimmed)}</p>;
      })}
    </article>
  );
};

const AdminHelp = ({ audience = 'admin' }) => {
  const [activeSlug, setActiveSlug] = useState('onboarding');
  const isCustomer = audience === 'customer';
  const { data, isLoading, error } = useQuery(
    [audience, 'help-docs'],
    () => (isCustomer ? customerAPI.getHelpDocs() : adminAPI.getHelpDocs()),
    {
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  );

  const docs = useMemo(() => data?.docs || [], [data?.docs]);
  const groupedDocs = useMemo(() => docs.reduce((groups, doc) => {
    const section = doc.section || 'Help';
    return {
      ...groups,
      [section]: [...(groups[section] || []), doc]
    };
  }, {}), [docs]);
  const activeDoc = docs.find((doc) => doc.slug === activeSlug) || docs[0];

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return <ErrorAlert message="Failed to load help docs" />;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-slate-950 px-6 py-8 text-white shadow-sm sm:px-8">
        <p className="text-sm font-medium uppercase tracking-[0.24em] text-blue-300">Help Center</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight">DataVisual 3PL+ Help and Guides</h1>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
          {isCustomer
            ? 'Guides and workflow notes for using the customer portal.'
            : 'Draft customer-facing help articles rendered inside the protected admin app for review.'}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="space-y-5">
            {Object.entries(groupedDocs).map(([section, sectionDocs]) => (
              <div key={section}>
                <h2 className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{section}</h2>
                <div className="space-y-1">
                  {sectionDocs.map((doc) => (
                    <button
                      key={doc.slug}
                      type="button"
                      onClick={() => setActiveSlug(doc.slug)}
                      className={`w-full rounded-xl px-3 py-2 text-left text-sm font-medium transition-colors ${
                        activeDoc?.slug === doc.slug
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950'
                      }`}
                    >
                      {doc.title}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </aside>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          {activeDoc ? (
            <MarkdownArticle content={activeDoc.content} />
          ) : (
            <p className="text-sm text-slate-500">No help articles found.</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminHelp;
